import { Share } from '@capacitor/share'
import {
  CheckCircleIcon,
  CopyIcon,
  EnvelopeSimpleIcon,
  ShareNetworkIcon,
  UserPlusIcon,
  WarningIcon,
} from '@phosphor-icons/react'
import { useState } from 'react'

import { Pastilla } from '@/components/Editorial'
import { HojaInferior } from '@/components/HojaInferior'
import { ApiError, type Invitation, household } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'

/**
 * Invitar a alguien al hogar.
 *
 * Vive en su propio archivo porque se usa desde dos sitios: la campana del
 * inicio, que es donde se busca cuando se quiere invitar en el momento, y la
 * sección de Ajustes, que es donde se busca cuando se está configurando la app.
 * Tenerlo duplicado habría hecho que un cambio en el texto legal —«cualquiera
 * con este enlace puede unirse»— se quedara en uno de los dos.
 *
 * El enlace no se genera al abrir sino al pedirlo: cada llamada crea una
 * invitación nueva en el servidor, y abrir la hoja para mirar no debería dejar
 * enlaces vivos por ahí.
 */
export function Invitacion({ onListo }: { onListo?: () => void }) {
  const [invitacion, setInvitacion] = useState<Invitation | null>(null)
  const [correo, setCorreo] = useState('')
  const [generando, setGenerando] = useState(false)
  const [copiado, setCopiado] = useState(false)
  const [reenviado, setReenviado] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const enlace = invitacion
    ? (invitacion.url ?? `${window.location.origin}/invitacion/${invitacion.token}`)
    : null

  const generar = async (): Promise<void> => {
    setGenerando(true)
    setError(null)
    try {
      const limpio = correo.trim()
      setInvitacion(
        await household.invitations.create({
          intendedRole: 'MEMBER',
          // Vacío significa «solo enlace»: mandar una cadena en blanco haría
          // que el servidor guardara una invitación dirigida a nadie.
          ...(limpio ? { email: limpio } : {}),
        }),
      )
      haptic('success')
      onListo?.()
    } catch (cause) {
      haptic('error')
      setError(cause instanceof ApiError ? cause.message : 'No pudimos crear la invitación.')
    } finally {
      setGenerando(false)
    }
  }

  const reenviar = async (): Promise<void> => {
    if (!invitacion) return
    setError(null)
    try {
      const { emailSent } = await household.invitations.resend(invitacion.id)
      if (emailSent) {
        haptic('success')
        setReenviado(true)
        setTimeout(() => setReenviado(false), 3000)
      } else {
        haptic('error')
        setError('Tampoco salió esta vez. Comparte el enlace por otro lado.')
      }
    } catch (cause) {
      haptic('error')
      setError(cause instanceof ApiError ? cause.message : 'No pudimos reenviarlo.')
    }
  }

  const copiar = async (): Promise<void> => {
    if (!enlace) return
    try {
      await navigator.clipboard.writeText(enlace)
      setCopiado(true)
      haptic('success')
      setTimeout(() => setCopiado(false), 2500)
    } catch {
      setError('No pudimos copiar el enlace. Selecciónalo y cópialo a mano.')
    }
  }

  const compartir = async (): Promise<void> => {
    if (!enlace) return
    haptic('light')
    try {
      // La hoja nativa de compartir expone WhatsApp, Telegram y Mensajes sin
      // integrar ninguna de esas APIs. En navegador cae a `navigator.share`, y
      // si tampoco existe, al portapapeles.
      await Share.share({
        title: 'Únete a nuestro PayBae',
        text: 'Te invito a llevar nuestras finanzas juntos en PayBae.',
        url: enlace,
      })
    } catch {
      await copiar()
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {!invitacion ? (
        <>
          <div className="flex items-center gap-3 rounded-card bg-surface px-3.5 py-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-text">
              <UserPlusIcon size={20} weight="fill" />
            </span>
            <p className="flex-1 text-[13px] leading-snug text-ink-soft">
              Quien reciba la invitación se une a tu hogar y podrá registrar gastos desde su
              propio teléfono.
            </p>
          </div>

          {/* El correo es opcional y va primero. Mandarlo por correo es lo que
              la mayoría espera de «invitar», pero el enlace suelto sigue
              existiendo porque en México se comparte por WhatsApp más que por
              correo, y a veces ni se sabe la dirección de la otra persona. */}
          <label className="flex flex-col gap-1.5">
            <span className="px-1 text-[12px] font-semibold text-ink-soft">
              Su correo <span className="font-normal text-ink-faint">— opcional</span>
            </span>
            <input
              type="email"
              inputMode="email"
              autoComplete="off"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              placeholder="nombre@correo.com"
              className="h-[52px] w-full rounded-card border border-transparent bg-surface-sunken px-3.5 text-[16px] text-ink outline-none transition-colors duration-150 placeholder:text-ink-faint focus:border-primary focus:bg-surface"
            />
          </label>

          <Pastilla onClick={() => void generar()} ocupado={generando}>
            {correo.trim() ? 'Mandar la invitación' : 'Crear enlace para compartir'}
          </Pastilla>
        </>
      ) : (
        <>
          {/* Qué pasó con el correo, cuando se pidió uno. Si Mailgun falló, se
              dice: el enlace sirve igual y compartirlo a mano es la salida. */}
          {invitacion.email ? (
            invitacion.emailSent ? (
              <div className="flex items-start gap-2.5 rounded-card bg-income-soft px-3.5 py-3">
                <EnvelopeSimpleIcon
                  size={18}
                  weight="fill"
                  className="mt-0.5 shrink-0 text-income-text"
                />
                <div className="flex-1">
                  <p className="text-[13px] leading-snug text-income-text">
                    Se la mandamos a <span className="font-semibold">{invitacion.email}</span>.
                  </p>
                  <button
                    type="button"
                    onClick={() => void reenviar()}
                    disabled={reenviado}
                    className="mt-1 min-h-[32px] text-[12px] font-semibold text-income-text underline underline-offset-2 active:opacity-60 disabled:no-underline disabled:opacity-70"
                  >
                    {reenviado ? 'Enviado otra vez' : '¿No le llegó? Reenviar'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 rounded-card bg-warning-soft px-3.5 py-3">
                <WarningIcon size={18} weight="fill" className="mt-0.5 shrink-0 text-warning-text" />
                <p className="flex-1 text-[13px] leading-snug text-warning-text">
                  No pudimos mandar el correo, pero la invitación ya existe. Comparte el enlace
                  de abajo y funciona igual.
                </p>
              </div>
            )
          ) : null}

          <p className="break-all rounded-card bg-surface-sunken px-3.5 py-3 font-mono text-[12px] text-ink-soft">
            {enlace}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void compartir()}
              className="flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-card bg-accion text-[14px] font-semibold text-on-accion active:scale-[0.98]"
            >
              <ShareNetworkIcon size={17} weight="bold" />
              Compartir
            </button>
            <button
              type="button"
              onClick={() => void copiar()}
              className={cn(
                'flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-card border text-[14px] font-semibold active:scale-[0.98]',
                copiado
                  ? 'border-income bg-income-soft text-income-text'
                  : 'border-line bg-surface text-ink',
              )}
            >
              {copiado ? <CheckCircleIcon size={17} weight="fill" /> : <CopyIcon size={17} />}
              {copiado ? 'Copiado' : 'Copiar'}
            </button>
          </div>
          <p className="flex items-start gap-1.5 text-[12px] leading-snug text-ink-faint">
            <WarningIcon size={14} className="mt-0.5 shrink-0" />
            Cualquiera con este enlace puede unirse. Compártelo solo con quien quieras dentro.
          </p>
        </>
      )}

      {error ? (
        <p role="alert" className="text-[12px] text-expense-text">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/** La misma invitación, dentro de la hoja que sube desde abajo. */
export function HojaInvitacion({
  abierta,
  onCerrar,
}: {
  abierta: boolean
  onCerrar: () => void
}) {
  return (
    <HojaInferior
      abierta={abierta}
      titulo="Invitar a alguien"
      descripcion="Comparte el enlace y quien lo abra entra a tu hogar."
      onCerrar={onCerrar}
    >
      <Invitacion />
    </HojaInferior>
  )
}
